// services/ecdh.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment as ENV } from 'environments/environment';

function toHex(arr: Uint8Array): string {
  var result = '';
  for (var i = 0; i < arr.length; i++) {
    var hex = arr[i].toString(16);
    result += (hex.length === 1 ? '0' + hex : hex);
  }
  return result;
}

function fromHex(hex: string): Uint8Array {
  var matches = hex.match(/.{1,2}/g) || [];
  var arr = new Uint8Array(matches.length);
  for (var i = 0; i < matches.length; i++) {
    arr[i] = parseInt(matches[i], 16);
  }
  return arr;
}

function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = Math.random() * 16 | 0;
    var v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

@Injectable({ providedIn: 'root' })
export class ECDHService {
  private aesKey: CryptoKey = null;
  private sessionId: string = generateUUID();
  private initialized = false;
  private _readyResolve: () => void;
  private _readyPromise: Promise<void>;

  constructor(private http: HttpClient) {
    var self = this;
    this._readyPromise = new Promise<void>(function (resolve) {
      self._readyResolve = resolve;
    });
  }

  initSession(): Promise<void> {
    return this._doInit();
  }

  whenReady(): Promise<void> {
    return this._readyPromise;
  }

  private _doInit(): Promise<void> {
    var self = this;

    // 1. Generate client ECDH key pair
    return (crypto.subtle.generateKey(
      { name: 'ECDH', namedCurve: 'P-256' } as any,
      true,
      ['deriveKey', 'deriveBits']
    ) as Promise<CryptoKeyPair>).then(function (clientKeyPair) {

      // 2. Export client public key as hex
      return (crypto.subtle.exportKey('raw', clientKeyPair.publicKey) as Promise<ArrayBuffer>).then(function (clientPublicKeyRaw) {
        var clientPublicKeyHex = toHex(new Uint8Array(clientPublicKeyRaw));

        // 3. Send to server, receive serverPublicKey
        return self.http.post<{ serverPublicKey: string }>(ENV.nodeUrl + '/key-exchange', {
          clientPublicKey: clientPublicKeyHex,
          sessionId: self.sessionId
        }).toPromise().then(function (response) {

          // 4. Import server public key
          var serverKeyBytes = fromHex(response.serverPublicKey);
          return (crypto.subtle.importKey(
            'raw',
            serverKeyBytes,
            { name: 'ECDH', namedCurve: 'P-256' } as any,
            false,
            []
          ) as Promise<CryptoKey>).then(function (serverCryptoKey) {

            // 5. Derive shared secret bits via ECDH
            return (crypto.subtle.deriveBits(
              { name: 'ECDH', public: serverCryptoKey } as any,
              clientKeyPair.privateKey,
              256
            ) as Promise<ArrayBuffer>).then(function (sharedBits) {

              // 6. Apply HKDF — MUST match server's salt and info exactly
              return (crypto.subtle.importKey('raw', sharedBits, { name: 'HKDF' } as any, false, ['deriveKey']) as Promise<CryptoKey>).then(function (hkdfKey) {
                return (crypto.subtle.deriveKey(
                  {
                    name: 'HKDF',
                    hash: 'SHA-256',
                    salt: new TextEncoder().encode('ecdh-salt-v1'),
                    info: new TextEncoder().encode('aes-256-gcm-key')
                  } as any,
                  hkdfKey,
                  { name: 'AES-GCM', length: 256 },
                  false,
                  ['encrypt', 'decrypt']
                ) as Promise<CryptoKey>).then(function (aesKey) {
                  self.aesKey = aesKey;
                  self.initialized = true;
                  self._readyResolve();
                });
              });
            });
          });
        });
      });
    });
  }

  encrypt(data: any): Promise<{ iv: string; data: string; tag: string }> {
    if (!this.aesKey) { throw new Error('Session not initialized'); }

    var iv = crypto.getRandomValues(new Uint8Array(16));
    var encoded = new TextEncoder().encode(JSON.stringify(data));
    var aesKey = this.aesKey;

    return (crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv } as any,
      aesKey,
      encoded
    ) as Promise<ArrayBuffer>).then(function (cipherBuffer) {
      var cipherBytes = new Uint8Array(cipherBuffer);
      var cipherData = cipherBytes.slice(0, cipherBytes.length - 16);  // everything except last 16 bytes
      var tag = cipherBytes.slice(cipherBytes.length - 16);            // last 16 bytes = GCM auth tag

      return {
        iv: toHex(iv),
        data: toHex(cipherData),
        tag: toHex(tag)
      };
    });
  }

  decrypt(payload: { iv: string; data: string; tag: string }): Promise<any> {
    if (!this.aesKey) { throw new Error('Session not initialized'); }

    var iv = fromHex(payload.iv);
    var cipherData = fromHex(payload.data);
    var tag = fromHex(payload.tag);

    // Web Crypto expects ciphertext + tag concatenated
    var combined = new Uint8Array(cipherData.length + tag.length);
    combined.set(cipherData);
    combined.set(tag, cipherData.length);

    return (crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv } as any,
      this.aesKey,
      combined
    ) as Promise<ArrayBuffer>).then(function (decryptedBuffer) {
      return JSON.parse(new TextDecoder().decode(decryptedBuffer));
    });
  }

  getSessionId(): string { return this.sessionId; }
  isInitialized(): boolean { return this.initialized; }
}
